import React, { createContext, useContext, useEffect, useState, ReactNode, Dispatch, SetStateAction } from 'react';
import {
  GridContextPersistenceName,
  gridContextPersistenceService,
} from '@components-library/Grid/gridContextPersistence.service';

export interface IGridFilterConfigItem {
  key: string;
  label?: string;
  visible?: boolean;
  enabled?: boolean;
  order?: number;
}

interface IGridFilterConfigContext {
  config: IGridFilterConfigItem[];
  setConfig: Dispatch<SetStateAction<IGridFilterConfigItem[]>>;
}

interface IGridFilterConfigProviderProps {
  children: ReactNode;
  gridName?: GridContextPersistenceName;
}

const GridFilterConfigContext = createContext<IGridFilterConfigContext | undefined>(undefined);

export const useGridFilterConfig = (): IGridFilterConfigContext => {
  const context = useContext(GridFilterConfigContext);

  if (!context) {
    throw new Error('useGridFilterConfig must be used within GridFilterConfigProvider');
  }
  return context;
};

export const GridFilterConfigProvider = ({ children, gridName }: IGridFilterConfigProviderProps) => {
  const storedConfig = gridName ? gridContextPersistenceService.getFiltersFromGridPersistanceStorage(gridName) : null;

  const [config, setConfig] = useState<IGridFilterConfigItem[]>(
    storedConfig
      ? storedConfig.order.map((key: string, index: number) => ({
          key,
          label: undefined,
          visible: storedConfig.visibility?.[key] ?? true,
          order: index + 1,
          enabled: true,
        }))
      : [],
  );

  useEffect(() => {
    if (gridName) {
      const visibilityState = Object.fromEntries(config.map((c) => [c.key, c.visible ?? true]));
      const orderState = [...config].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((c) => c.key);

      gridContextPersistenceService.saveFiltersToGridPersistanceStorage(gridName, {
        visibility: visibilityState,
        order: orderState,
      });
    }
  }, [config, gridName]);

  return <GridFilterConfigContext.Provider value={{ config, setConfig }}>{children}</GridFilterConfigContext.Provider>;
};
